import React, { useMemo, useState } from "react";
import { Tooltip, Typography, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MUIButton from "../../../Component/UI/MUIButton/MUIButton";
import { OVERVIEW_LLM_LABELS } from "../../../utils/constant";

const llmColorsMapping = {
  "gpt-4o": "#C7F0EC",
  Claude3: "#FF6F6F",
  Gemini: "#CEB4FF",
  llama3: "#FFCE6E",
  Perplexity: "#92B7FE",
  "llm-web": "#FEE7E6",
  "gpt-4o-web-search": "lightgreen",
  "GeminiV25": "#CF9FFF",
};

const colors = [
  "#C7F0EC",
  "#FF6F6F",
  "#CEB4FF",
  "#FFCE6E",
  "#92B7FE",
  "#FEE7E6",
  "#CEB4FF",
  "#FFCE6E",
];

const fallbackColorMap = {};

const generateColorForLLM = (llm) => {
  if (!fallbackColorMap[llm]) {
    const colorIndex = Object.keys(fallbackColorMap).length % colors.length;
    fallbackColorMap[llm] = colors[colorIndex];
  }
  return fallbackColorMap[llm];
};

const processData = (data = [], sourceAlias = {}) => {
  if (!Array.isArray(data) || typeof sourceAlias !== "object") {
    return []; // Ensure function returns an empty array if inputs are invalid
  }

  return data
    .filter((obj) => obj && typeof obj === "object")
    .map((item) =>
      Object.fromEntries(
        Object.entries(item || {}).filter(
          ([_, value]) => value !== null && value !== undefined
        )
      )
    );
};

const ReTableView = ({ data, sourceAlias, type, renderValidate = true }) => {
  const [visibleRows, setVisibleRows] = useState(15);

  const filteredData = useMemo(
    () => processData(data, sourceAlias),
    [data, sourceAlias]
  );

  const sortedData = useMemo(() => {
    return [...filteredData].filter((obj) => !obj?.name.includes('vertexaisearch'))?.sort((a, b) => {
      const totalA = Object.keys(a).reduce(
        (sum, key) => (key !== "name" && a[key] ? sum + Number(a[key]) : sum),
        0
      );
      const totalB = Object.keys(b).reduce(
        (sum, key) => (key !== "name" && b[key] ? sum + Number(b[key]) : sum),
        0
      );
      return totalB - totalA; // Descending order
    });
  }, [filteredData]);

  const allLLMKeys = useMemo(() => {
    const keysSet = new Set();

    filteredData?.forEach((item) => {
      Object.keys(item || {}).forEach((key) => {
        if (key !== "name") keysSet.add(key);
      });
    });

    return Array.from(keysSet);
  }, [filteredData]);

  const handleShowMore = () => {
    setVisibleRows((prev) => prev + 15);
  };

  const maxTotalValue = useMemo(() => {
    return Math.max(
      ...sortedData.map((item) =>
        Object.keys(item).reduce(
          (sum, key) =>
            key !== "name" && item[key] ? sum + Number(item[key]) : sum,
          0
        )
      ),
      1
    );
  }, [sortedData]);

  const renderProgressBar = (item) => {
    const totalValue = Object.keys(item).reduce(
      (sum, key) =>
        key !== "name" && item[key] ? sum + Number(item[key]) : sum,
      0
    );

    const barWidth = `${(totalValue / maxTotalValue) * 100}%`;

    return (
      <div
        style={{
          display: "flex",
          height: 10,
          borderRadius: 5,
          overflow: "hidden",
          width: barWidth,
          backgroundColor: "#e0e0e0",
          position: "relative",
          zIndex: 1,
        }}
      >
        {allLLMKeys?.map((llm, i) => (
          <Tooltip
            key={i}
            title={
              <div style={{ textAlign: "left" }}>
                <Typography style={{ fontSize: 14, fontWeight: "bold" }}>
                  {OVERVIEW_LLM_LABELS?.[llm] || llm}
                </Typography>
                <div
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    backgroundColor:
                      llmColorsMapping?.[llm] || generateColorForLLM(llm),
                    marginRight: 5,
                    borderRadius: "50%",
                  }}
                />
                <Typography style={{ display: "inline", fontSize: 14 }}>
                  {item[llm]}
                </Typography>
              </div>
            }
            arrow
            placement="top"
          >
            <div
              style={{
                width: `${(item[llm] / totalValue) * 100}%`,
                backgroundColor:
                  llmColorsMapping?.[llm] || generateColorForLLM(llm),
                height: "100%",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        ))}
      </div>
    );
  };

  const renderSourceCell = (item, sourceLinks) => {
    const validLinks = sourceLinks.filter((link) => link.http_status === "200");
    const isForbidden = sourceLinks?.every(
      (link) => link.http_status === "403"
    );

    return (
      <Tooltip
        title={
          validLinks.length > 0 ? (
            <div>
              {validLinks.map((link, index) => (
                <div key={index}>
                  <Typography
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "block",
                      color: "white",
                      textDecoration: "underline",
                      cursor: "pointer",
                      marginBottom: 4,
                    }}
                  >
                    {link.url}
                  </Typography>
                  {index < validLinks.length - 1 && (
                    <hr style={{ borderColor: "white", margin: "6px 0" }} />
                  )}
                </div>
              ))}
            </div>
          ) : (
            ""
          )
        }
        placement="top-start"
        arrow
        disableInteractive={false}
      >
        <Typography
          style={{
            color: isForbidden ? "gray" : "blue",
            cursor: isForbidden ? "not-allowed" : "pointer",
            textDecoration: isForbidden ? "none" : "underline",
          }}
        >
          {item.name}
        </Typography>
      </Tooltip>
    );
  };

  const headers = [
    { label: "Source", width: "25%" },
    { label: "Source Frequency", width: "30%" },
    // { label: "Status", width: "15%" },
  ];
  /*if we dont want to show  Validated column */
  if (renderValidate) {
    headers.push({ label: "Validated", width: "15%" });
  }

  console.log('sortedData', sortedData);

  return (
    <>
      <h5 className="mt-2 mb-2 text-center">{`${type} Query Sources`}</h5>
      <div
        id={type.toLowerCase() + "_id"}
        style={{
          maxHeight: "500px",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <table className="table table-striped" style={{ width: "100%" }}>
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 2,
            }}
          >
            <tr className="prdtblHeader">
              {headers.map((header, index) => (
                <th
                  key={header.label}
                  style={{
                    width: header.width,
                    textAlign: index === 0 ? "left" : "center",
                    padding: "10px",
                  }}
                >
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!!sortedData.length ? (
              sortedData.slice(0, visibleRows).map((item, index) => (
                <tr key={index}>
                  <td style={{ width: "25%", textAlign: "left" }}>
                    {renderSourceCell(item, sourceAlias[item.name] || [])}
                  </td>
                  <td style={{ width: "30%", textAlign: "center" }}>
                    {renderProgressBar(item)}
                  </td>
                  {renderValidate ? <td style={{ width: "15%", textAlign: "center" }}>
                    {sourceAlias?.[item.name]?.every(
                      (link) => link.http_status === "403"
                    ) ? (
                      <CancelIcon style={{ color: "#FC4C4E" }} />
                    ) : (
                      <CheckCircleIcon style={{ color: "#36CFC2" }} />
                    )}
                  </td>
                    :
                    null}

                  {/* <td style={{ width: "15%", textAlign: "center" }}>
                    {sourceAlias?.[item.name]?.every(
                      (link) => link.http_status === "403"
                    ) ? (
                      <>
                        <CancelIcon style={{ color: "#FC4C4E" }} /> Blocked
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon style={{ color: "#36CFC2" }} /> Active
                      </>
                    )}
                  </td> */}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {visibleRows < sortedData.length && (
          <div
            style={{ textAlign: "center", padding: "10px", fontWeight: "400" }}
          >
            <MUIButton variant="text" onClick={handleShowMore}>
              Load More
            </MUIButton>
          </div>
        )}
      </div>
    </>
  );
};

export default ReTableView;
